import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const WorkspaceContext = createContext(null);

const DEFAULT_BRAND = {
  logo_text: "EPL",
  name: "EP-LearningHub",
  tagline: "Onboarding Portal",
  primary_color: "#0B3D2E",
  bg_color: "#F7F4EC",
  surface2_color: "#EFEADB",
  hero_eyebrow: "Sales Education · Wealth Creation",
  hero_title: "From day one to top performance in 14 days.",
  testimonial_quote: "The best hire-to-close ramp I have ever run.",
  testimonial_author: "Todd, Solution Engineer",
};

function applyBrand(brand) {
  const root = document.documentElement.style;
  const primary = brand.primary_color || DEFAULT_BRAND.primary_color;
  root.setProperty("--brand-primary", primary);
  root.setProperty("--brand", primary);
  root.setProperty(
    "--brand-hover",
    brand.primary_hover || `color-mix(in srgb, ${primary} 82%, black)`
  );
  root.setProperty("--brand-bg", brand.bg_color || DEFAULT_BRAND.bg_color);
  root.setProperty("--brand-surface2", brand.surface2_color || DEFAULT_BRAND.surface2_color);
}

function subdomainSlug() {
  const host = window.location.hostname; // e.g. proptix.learninghub.emmaus-partners.com
  const parts = host.split(".");
  if (parts.length >= 4 && parts[1] === "learninghub") return parts[0];
  return null;
}

function resolveSlug(user) {
  const q = new URLSearchParams(window.location.search).get("w");
  const sub = subdomainSlug();
  // Super-admins can target any workspace; localStorage/query/subdomain override wins.
  if (user && user.role === "super_admin") {
    return q || localStorage.getItem("wp_ws_slug") || sub || user.workspace_slug || "emmaus";
  }
  // Normal users are locked to their own workspace.
  if (user && user.workspace_slug) return user.workspace_slug;
  // Guests: subdomain > query > stored > default (drives landing/login branding).
  if (sub) return sub;
  if (q) return q;
  return localStorage.getItem("wp_ws_slug") || "emmaus";
}

export function WorkspaceProvider({ children }) {
  const { user, ready } = useAuth();
  const [workspace, setWorkspace] = useState(null);
  const [brand, setBrand] = useState(DEFAULT_BRAND);

  const load = useCallback(async () => {
    const slug = resolveSlug(user);
    localStorage.setItem("wp_ws_slug", slug);
    try {
      const { data } = await api.get("/workspaces/current");
      // Sync to the backend-resolved workspace (handles custom-domain hosts).
      if (data.slug) localStorage.setItem("wp_ws_slug", data.slug);
      setWorkspace(data);
      const merged = { ...DEFAULT_BRAND, name: data.name, ...(data.brand || {}) };
      setBrand(merged);
      applyBrand(merged);
    } catch {
      setBrand(DEFAULT_BRAND);
      applyBrand(DEFAULT_BRAND);
    }
  }, [user]);

  useEffect(() => {
    if (ready) load();
  }, [ready, load]);

  return (
    <WorkspaceContext.Provider value={{ workspace, brand, reload: load }}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export const useWorkspace = () => useContext(WorkspaceContext) || { brand: DEFAULT_BRAND, workspace: null };
